<template>
  <UIForm class="FormContact" @submit.prevent="onSubmit">
    <UIInputText
      name="fullName"
      label="Full name"
      isRequired
      placeholder="Full name"
    />
    <UIInputText
      name="companyName"
      label="Company name"
      isRequired
      placeholder="Company name"
    />
    <UIInputText
      name="email"
      label="Work email"
      isRequired
      type="email"
      placeholder="you@company.com"
    />
    <UIInputText
      name="phone"
      label="Phone number"
      type="tel"
      placeholder="+370"
    />
    <UIInputTextarea
      name="message"
      label="How can we help?"
      :rows="3"
      :maxLength="150"
      :showCharacterCounter="true"
      placeholder="e.g., I have a question about your pricing for large companies, and I'd like to schedule a brief demo."
    />
    <UIInputCheckbox name="agreeToPrivacy" isRequired>
      <template #label>
        <div>
          By submitting, you agree to our
          <RouterLink :to="{ path: '/privacy-policy' }">
            Privacy Policy
          </RouterLink>
        </div>
      </template>
    </UIInputCheckbox>

    <UIButton type="submit" isFull size="xl" :isLoading="isLoading">
      Get in touch
    </UIButton>
  </UIForm>
</template>

<script setup lang="ts">
import UIButton from '../ui/buttons/UIButton.vue'
import UIInputText from '../ui/inputs/UIInputText.vue'
import UIInputTextarea from '../ui/inputs/UIInputTextarea.vue'
import UIInputCheckbox from '../ui/inputs/UIInputCheckbox.vue'
import UIForm from '../ui/forms/UIForm.vue'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import { ref } from 'vue'
import { useToast } from '@/store/toast'
import { omnisend } from '@/lib/omnisend'

const emit = defineEmits(['submit', 'close'])
const props = defineProps<{
  skipSuccessToast?: boolean
}>()

const { handleSubmit, resetForm } = useForm({
  validationSchema: yup.object({
    fullName: yup.string().required('Full name is required'),
    companyName: yup.string().required('Company name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup.string().optional(),
    message: yup.string(),
    agreeToPrivacy: yup
      .boolean()
      .required('You must agree to the privacy policy'),
  }),
})
const isLoading = ref(false)
const onSubmit = handleSubmit((values) => {
  if (isLoading.value) return

  isLoading.value = true
  omnisend
    .subscribe(
      {
        firstName: values.fullName,
        email: values.email,
        phone: values.phone,
        customProperties: {
          message: values.message,
          companyName: values.companyName,
        },
      },
      ['contact'],
    )
    .then(() => {
      if (!props.skipSuccessToast) {
        useToast().onSuccess('Demo request submitted successfully')
      }
      resetForm()
      emit('submit')
    })
    .finally(() => {
      isLoading.value = false
      emit('close')
    })
})
</script>

<style lang="scss" scoped>
.FormContact {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1rem;

  @include md {
    gap: 1.5rem 1.5rem;
  }

  // Make email, textarea, checkbox, and button span full width
  :nth-child(5),
  :nth-child(6) {
    grid-column: 1 / -1;
  }
}
</style>
